/**
 * Ask-case-question modal (#613, ADR-0150) — LF co-asks a handling firm from the
 * case detail panel. The closed 3-topic menu + an optional note, posted through
 * the CC worker's ask proxy. No global compose (the case panel is the only ask
 * door); the button that opens this is gated Placed-and-beyond.
 *
 * A Case Question is a one-time typed question, not a conversation. Copy states
 * the blast radius plainly — it posts to Slack and the firm answers once.
 */

import { useState } from "react";
import {
  CASE_QUESTION_NOTE_MAX,
  CASE_QUESTION_SENTENCES,
  CASE_QUESTION_TYPES,
  type CaseQuestionType,
} from "@command-center/shared";
import { useAskCaseQuestion } from "@/lib/use-case-questions";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";

export function AskCaseQuestionModal({
  open,
  onClose,
  placementId,
  clientName,
  testId = "ask-case-question",
}: {
  open: boolean;
  onClose: () => void;
  placementId: string;
  clientName: string;
  testId?: string;
}) {
  // Remount on every open so an abandoned draft never leaks into the next ask.
  if (!open) return null;
  return (
    <AskForm onClose={onClose} placementId={placementId} clientName={clientName} testId={testId} />
  );
}

function AskForm({
  onClose,
  placementId,
  clientName,
  testId,
}: {
  onClose: () => void;
  placementId: string;
  clientName: string;
  testId: string;
}) {
  const [type, setType] = useState<CaseQuestionType | "">("");
  const [note, setNote] = useState("");
  const ask = useAskCaseQuestion();

  const canSend = type !== "" && note.length <= CASE_QUESTION_NOTE_MAX && !ask.isPending;

  function send() {
    // `canSend` includes `type !== ""`, so TS narrows `type` to a real topic here.
    if (!canSend) return;
    ask.mutate(
      {
        placement_id: placementId,
        question_type: type,
        note: note.trim() === "" ? undefined : note.trim(),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog open onClose={onClose} labelledBy={`${testId}-title`} testId={testId}>
      <DialogHeader>
        <DialogTitle id={`${testId}-title`}>Ask the handling firm</DialogTitle>
        <DialogDescription>
          Asks the handling firm on {clientName}'s case a typed question. Posts to Slack; the firm
          answers once — not a conversation. For anything else, message the firm.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">Topic</span>
          <NativeSelect
            value={type}
            onChange={(e) => setType(e.target.value as CaseQuestionType | "")}
            data-testid={`${testId}-type`}
          >
            <option value="">— Select a topic —</option>
            {CASE_QUESTION_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </NativeSelect>
          {type !== "" ? (
            <span className="text-xs italic text-plane-muted" data-testid={`${testId}-preview`}>
              {CASE_QUESTION_SENTENCES[type]}
            </span>
          ) : null}
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">Note (optional)</span>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={CASE_QUESTION_NOTE_MAX}
            data-testid={`${testId}-note`}
          />
        </label>

        {ask.isError ? (
          <p className="text-sm text-error" data-testid={`${testId}-error`}>
            Couldn't send the question — the case may already have an open LF question, or the case
            isn't placed. Try again.
          </p>
        ) : null}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button onClick={send} disabled={!canSend} data-testid={`${testId}-send`}>
          {ask.isPending ? "Sending…" : "Send question"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
